/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, Component, Inject, NgZone, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DomSanitizer} from '@angular/platform-browser';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs';
import {StateService, UIRouter} from '@uirouter/core';

import template from './sign-in-modal.component.html';
import {AdnTranslateService} from '../common/filters/adn-translate.service';
import {SignInFormBase} from './sign-in-form.base';
import {AdnAuthHelperService} from '../../components/util/adn-auth-helper.service';
import {AuthService} from '../../components/auth/auth.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {WebAuthnService} from '../common/controller/web-authn.service';
import {BrowserLocationService, WINDOW} from '../angular-platform-providers';
import {ExceptionHandlerService} from '../errors/exception-handler.service';
import {DocumentShellService} from '../base/document-shell.service';
import {AppEventBusService} from '../common/events/app-event-bus.service';
import {ApiConfig} from '../config/api-config';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {FormsModule} from '@angular/forms';
import {APP_SHARED} from '../common/app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule],
  selector: 'adn-sign-in-modal',
  template
})
export class SignInModalComponent extends SignInFormBase implements OnInit {
  /** Set via BsModalService initialState */
  rejection: any;

  readonly sessionResult = new Subject<any>();

  constructor(
    public bsModalRef: BsModalRef,
    ngZone: NgZone,
    sanitizer: DomSanitizer,
    adnTranslate: AdnTranslateService,
    @Inject(WINDOW) window: any,
    @Inject(DOCUMENT) document: any,
    eventBus: AppEventBusService,
    @Inject(BrowserLocationService) location: any,
    $state: StateService,
    uiRouter: UIRouter,
    translate: TranslateService,
    exceptionHandler: ExceptionHandlerService,
    @Inject(AuthService) AuthService: any,
    @Inject(AdnAuthHelperService) adnAuthHelper: any,
    webAuthnResource: WebAuthnService,
    @Inject(LocalUserProfile) localUserProfile: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    documentShell: DocumentShellService,
    apiConfig: ApiConfig,
    localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(
      ngZone, sanitizer, adnTranslate, window, document, eventBus, location, $state, uiRouter,
      translate, exceptionHandler, AuthService, adnAuthHelper, webAuthnResource,
      localUserProfile, LocalNetworkProfile, LocalUserPermissions, documentShell, apiConfig, localNetworkInfo
    );
  }

  ngOnInit(): void {
    this.initSharedState({rejection: this.rejection, isModal: true});
  }

  get prefersPasskey(): boolean {
    return this.view.submitMethod === 'signIn'
      && !!this.credentials.passkeyLast
      && this.showLoginWithPasskey;
  }

  get showPasswordFields(): boolean {
    return !!this.view.password && !this.prefersPasskey;
  }

  get showPasskeyButton(): boolean {
    return !!this.view.passkeyButton && this.showLoginWithPasskey;
  }

  get showSubmitButton(): boolean {
    return !!this.view.buttonText && !this.prefersPasskey;
  }

  onSubmit(event: Event): void {
    if (this.view.submitMethod === 'signIn') {
      this.signIn(this.credentials, event, this.credentials.masqLast);
      return;
    }
    super.onSubmit(event);
  }

  forgotPasswordAndClearModal(): void {
    this.sessionResult.next(false);
    this.sessionResult.complete();
    this.bsModalRef.hide();
    this.$state.go('sign-in.password-reset', {}, {reload: true, inherit: false});
  }

  goSignIn(): void {
    this.sessionResult.next(false);
    this.sessionResult.complete();
    this.bsModalRef.hide();
    this.$state.go('sign-in', {}, {reload: true, inherit: false});
  }

  protected onAuthSuccess(session: any): void {
    this.sessionResult.next(session);
    this.sessionResult.complete();
    this.bsModalRef.hide();
  }
}
